<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type {
  ConsoleType,
  ConsolePayloadType,
} from "@/common/types/console/console";

import {
  baseDate,
  baseInput,
  baseSelect,
  baseTextEditor,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: ConsoleType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  name: "",
  type: "CONSOLE",
  status: "INACTIVE",
  is_main: 0,
  is_official: 0,
  is_system: 0,
  description: "",
};

const payload = ref<ConsolePayloadType>({ ...DEFAULT_DATA });

const optionsStatus = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

const optionsType = computed(() => [
  {
    option: t("common.labels.arcade"),
    value: "ARCADE",
  },
  {
    option: t("common.labels.console"),
    value: "CONSOLE",
  },
  {
    option: t("common.labels.microcomputer"),
    value: "MICROCOMPUTER",
  },
]);

const optionsYesNot = computed(() => [
  {
    option: t("common.labels.yes"),
    value: 1,
  },
  {
    option: t("common.labels.not"),
    value: 0,
  },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, status, is_official, is_system, is_main, description } =
    props.data;

  payload.value = {
    ...payload.value,
    name,
    status,
    is_main: is_main,
    is_official: is_official,
    is_system: is_system,
    description,
  };
}

function maskSlug(data: string) {
  return data?.replaceAll(" ", "-")?.toLowerCase();
}

function maskYear(data: string) {
  return data?.replace(/\D/g, "")?.replaceAll(" ", "");
}

const debouceUpdate = debounce(update, 500);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-media">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('media.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("media.labels.name") }} * </template>
    </base-input>

    <div class="flex gap-4">
      <base-select
        v-model="payload.status"
        :value="payload.status"
        :options="optionsStatus"
        :is-empty="false"
        @change="debouceUpdate('status')"
      >
        <template #label> {{ t("common.labels.status") }}</template>
      </base-select>

      <base-select
        v-model="payload.type"
        :value="payload.type"
        :options="optionsType"
        :is-empty="false"
        @change="debouceUpdate('type')"
      >
        <template #label> Plataforma </template>
      </base-select>
    </div>

    <div class="flex gap-4">
      <base-select
        v-model="payload.is_main"
        :value="payload.is_main"
        :options="optionsYesNot"
        :is-empty="false"
        @change="debouceUpdate('is_main')"
      >
        <template #label> {{ t("collection.labels.is_main") }}</template>
      </base-select>

      <base-select
        v-model="payload.is_system"
        :value="payload.is_system"
        :options="optionsYesNot"
        :is-empty="false"
        @change="debouceUpdate('is_system')"
      >
        <template #label> É um Sistema? </template>
      </base-select>

      <base-select
        v-model="payload.is_official"
        :value="payload.is_official"
        :options="optionsYesNot"
        :is-empty="false"
        @change="debouceUpdate('is_official')"
      >
        <template #label> {{ t("collection.labels.is_official") }}</template>
      </base-select>
    </div>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("media.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-media {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
